
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Brain, Target } from 'lucide-react';

interface AIParameter {
  key: string;
  value: number;
  description: string;
}

export const AIParametersEditor = () => {
  const [parameters, setParameters] = useState<AIParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadParameters();
  }, []);

  const loadParameters = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value, description')
        .in('setting_key', ['ai_creativity_level', 'ai_focus_breadth']);

      if (error) throw error;

      const params = data.map(item => ({
        key: item.setting_key,
        value: parseFloat(item.setting_value),
        description: item.description || ''
      }));

      setParameters(params);
    } catch (error: any) {
      console.error('Error loading AI parameters:', error);
      toast({
        title: "Feil ved lasting av AI-parametere",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateParameter = (key: string, value: number) => {
    setParameters(prev => 
      prev.map(param => 
        param.key === key ? { ...param, value } : param
      )
    );
  };

  const saveParameters = async () => {
    setSaving(true);
    try {
      for (const param of parameters) {
        const { error } = await supabase
          .from('system_settings')
          .update({ 
            setting_value: param.value.toString(),
            updated_at: new Date().toISOString()
          })
          .eq('setting_key', param.key);

        if (error) throw error;
      }

      toast({
        title: "AI-parametere lagret",
        description: "Endringene trer i kraft ved neste AI-samtale",
      });
    } catch (error: any) {
      console.error('Error saving AI parameters:', error);
      toast({
        title: "Feil ved lagring",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getCreativityLabel = (value: number) => {
    if (value <= 0.3) return 'Lav (Forutsigbar)';
    if (value <= 0.7) return 'Middels (Balansert)';
    return 'Høy (Kreativ)';
  };

  const getFocusLabel = (value: number) => {
    if (value <= 0.7) return 'Smal (Fokusert)';
    if (value <= 0.9) return 'Middels (Balansert)';
    return 'Bred (Utforskende)';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Laster AI-parametere...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const creativityParam = parameters.find(p => p.key === 'ai_creativity_level');
  const focusParam = parameters.find(p => p.key === 'ai_focus_breadth');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          AI-oppførsel
        </CardTitle>
        <CardDescription>
          Juster hvordan AI-assistenten svarer og resonerer
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {creativityParam && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-blue-500" />
              <Label className="text-sm font-medium">
                Kreativitetsnivå: {getCreativityLabel(creativityParam.value)}
              </Label>
            </div>
            <Slider
              value={[creativityParam.value]}
              onValueChange={([value]) => updateParameter('ai_creativity_level', value)}
              min={0.1}
              max={1.0}
              step={0.1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Forutsigbar (0.1)</span>
              <span>Balansert (0.5)</span>
              <span>Kreativ (1.0)</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Kontrollerer hvor kreative og varierte svarene skal være
            </p>
          </div>
        )}

        {focusParam && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-green-500" />
              <Label className="text-sm font-medium">
                Fokusbredde: {getFocusLabel(focusParam.value)}
              </Label>
            </div>
            <Slider
              value={[focusParam.value]}
              onValueChange={([value]) => updateParameter('ai_focus_breadth', value)}
              min={0.1}
              max={1.0}
              step={0.05}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Smal (0.1)</span>
              <span>Middels (0.8)</span>
              <span>Bred (1.0)</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Kontrollerer hvor bredt AI-en vurderer forskjellige svarmuligheter
            </p>
          </div>
        )}

        <Button 
          onClick={saveParameters} 
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Lagrer...
            </>
          ) : (
            'Lagre AI-parametere'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
